/**
 * RegistrarConfigCard - Subset of check-in config editable by clinic registrar (Phase 4).
 * Welcome message and instructions. Chartmind-supervisor and clinic-registrar can edit.
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { getRegistrarConfig, setRegistrarConfig } from '@healthdesk/shared-services';

const RegistrarConfigCard = ({ clinicId }) => {
  const [config, setConfig] = useState({ welcomeMessage: '', instructions: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!clinicId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    getRegistrarConfig(clinicId)
      .then((data) => {
        if (!cancelled) setConfig({ welcomeMessage: data.welcomeMessage || '', instructions: data.instructions || '' });
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [clinicId]);

  const handleSave = async () => {
    if (!clinicId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await setRegistrarConfig(clinicId, config);
      setSuccess('Saved.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!clinicId) return null;
  if (loading) {
    return (
      <Card sx={{ mt: 2 }}>
        <CardContent><CircularProgress size={24} /> Loading...</CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        title="Registrar settings"
        subheader="Welcome message and instructions shown to patients at check-in. Editable by clinic registrar and supervisor."
      />
      <CardContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <TextField
          label="Welcome message"
          fullWidth
          multiline
          rows={3}
          value={config.welcomeMessage}
          onChange={(e) => setConfig((prev) => ({ ...prev, welcomeMessage: e.target.value }))}
          placeholder="e.g., Welcome to our clinic. Please complete the form below."
          sx={{ mb: 2 }}
        />
        <TextField
          label="Instructions (optional)"
          fullWidth
          multiline
          rows={2}
          value={config.instructions}
          onChange={(e) => setConfig((prev) => ({ ...prev, instructions: e.target.value }))}
          placeholder="Additional instructions for patients"
          sx={{ mb: 2 }}
        />
        <Button variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <Save />} onClick={handleSave} disabled={saving}>
          Save
        </Button>
      </CardContent>
    </Card>
  );
};

export default RegistrarConfigCard;
